import { dimensionKey, type ResultEnvelope, type SemanticQueryInput, type TimeGrain, type TimeRange } from "@cd/core";
import { formatTimeBucket } from "@cd/viz";
import { describeRange } from "./time";

/**
 * A date bucket clicked on a dashboard: the page shows just its dates until it's cleared. Page state
 * only, like the chips: it lives in the URL and never changes the saved date range.
 */
/**
 * Marks URL state written since dates became inclusive. Older links (no `v`) stored an exclusive end,
 * Oct 1 to Nov 1 for October, which would read a day long now, so they are dropped, not guessed at.
 */
export const DATES_V = 2;

export interface DateZoom {
  v: typeof DATES_V;
  /** Venue-local dates, "YYYY-MM-DD", both included. */
  range: TimeRange;
  /** The grain of the clicked bucket. Widgets at this grain or coarser show the next finer one. */
  grain: ZoomGrain;
  /** "Oct 2026", or the dates when the bucket was cut short by the page's range ("Sep 21 – Sep 30"). */
  label: string;
}

/** Grains whose buckets can be zoomed into. An hour is the finest grain; day of week isn't a span of dates. */
export const ZOOM_GRAINS = ["day", "week", "month", "quarter", "year"] as const;
export type ZoomGrain = (typeof ZOOM_GRAINS)[number];

/** Finest first; day of week is left out, since it isn't a span of time. */
const ORDER: TimeGrain[] = ["hour", "day", "week", "month", "quarter", "year"];

/** The grain a widget's time axis takes once zoomed into a bucket of `grain`. */
const NEXT_FINER: Record<ZoomGrain, TimeGrain> = { year: "month", quarter: "month", month: "day", week: "day", day: "hour" };

export const isZoomGrain = (g: unknown): g is ZoomGrain => ZOOM_GRAINS.includes(g as ZoomGrain);

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const utc = (day: string) => new Date(`${day}T00:00:00Z`);
const ymd = (d: Date) => d.toISOString().slice(0, 10);

/** The dates a bucket covers, from its raw value (its first day, maybe with a midnight time). */
export function bucketRange(value: string, grain: ZoomGrain): TimeRange | undefined {
  const start = value.slice(0, 10);
  if (!DAY.test(start) || Number.isNaN(utc(start).getTime())) return undefined;
  const d = utc(start);
  const y = d.getUTCFullYear(), m = d.getUTCMonth();
  const end =
    grain === "day" ? new Date(Date.UTC(y, m, d.getUTCDate() + 1))
      : grain === "week" ? new Date(Date.UTC(y, m, d.getUTCDate() + 7))
        : grain === "month" ? new Date(Date.UTC(y, m + 1, 1))
          : grain === "quarter" ? new Date(Date.UTC(y, m + 3, 1))
            : new Date(Date.UTC(y + 1, 0, 1));
  return { start, end: ymd(new Date(end.getTime() - 86_400_000)) }; // the bucket's last day
}

/** All the dates a result covers: locations in different time zones can each have their own. */
export function shownRange(r: ResultEnvelope | undefined): TimeRange | undefined {
  const all = r?.provenance.timeRanges?.map((t) => t.range) ?? (r?.provenance.timeRange ? [r.provenance.timeRange] : []);
  if (!all.length) return undefined;
  return { start: all.map((t) => t.start).sort()[0]!, end: all.map((t) => t.end).sort().at(-1)! };
}

/**
 * The zoom for a clicked bucket, cut to the range the widget showed, so the number clicked is the
 * number the zoomed page shows (clicking September on the last 30 days zooms to the days of
 * September in them). Undefined when the bucket is outside that range.
 */
export function zoomTo(value: unknown, grain: ZoomGrain, shown: TimeRange | undefined): DateZoom | undefined {
  if (typeof value !== "string") return undefined;
  const bucket = bucketRange(value, grain);
  if (!bucket) return undefined;
  const start = shown && shown.start.slice(0, 10) > bucket.start ? shown.start.slice(0, 10) : bucket.start;
  const end = shown && shown.end.slice(0, 10) < bucket.end ? shown.end.slice(0, 10) : bucket.end;
  if (start > end) return undefined;
  const whole = start === bucket.start && end === bucket.end;
  return { v: DATES_V, range: { start, end }, grain, label: whole ? formatTimeBucket(value, grain) : describeRange({ start, end }) };
}

/**
 * A widget's query while zoomed: time axes at the zoom's grain or coarser step down to the next
 * finer grain the field allows, so zooming into October shows its days rather than one bar. Axes
 * already finer, and day of week, stay as they are. `grainsOf` gives a field's allowed grains, or
 * undefined when they aren't known yet (nothing steps). Sorts follow the renamed columns.
 */
export function zoomQuery(query: SemanticQueryInput, zoom: DateZoom, grainsOf: (ref: string) => readonly string[] | undefined): SemanticQueryInput {
  // The next finer grain, else the closest finer one the field allows.
  const candidates = ORDER.slice(0, ORDER.indexOf(NEXT_FINER[zoom.grain]) + 1).reverse();
  const renamed = new Map<string, string>();
  const dimensions = (query.dimensions ?? []).map((d) => {
    const at = d.grain ? ORDER.indexOf(d.grain) : -1;
    if (at < ORDER.indexOf(zoom.grain)) return d;
    const allowed = grainsOf(d.ref);
    const grain = candidates.find((g) => allowed?.includes(g));
    if (!grain || grain === d.grain) return d;
    const next = { ...d, grain };
    renamed.set(dimensionKey(d), dimensionKey(next));
    return next;
  });
  if (!renamed.size) return query;
  return { ...query, dimensions, sort: query.sort?.map((s) => ({ ...s, key: renamed.get(s.key) ?? s.key })) };
}

export const isDay = (v: unknown): v is string => typeof v === "string" && DAY.test(v) && !Number.isNaN(utc(v).getTime());

/** The `z` search param. A hand-edited or stale one is dropped, never an error. */
export function parseZoom(z: unknown): DateZoom | undefined {
  if (!z || typeof z !== "object") return undefined;
  const c = z as Record<string, unknown>;
  const range = c.range as Record<string, unknown> | undefined;
  if (c.v !== DATES_V || !range || !isDay(range.start) || !isDay(range.end) || range.start > range.end) return undefined;
  if (!isZoomGrain(c.grain) || typeof c.label !== "string" || c.label.length > 60) return undefined;
  return { v: DATES_V, range: { start: range.start, end: range.end }, grain: c.grain, label: c.label };
}
